import { TableCell, TDocumentDefinitions } from "pdfmake/interfaces";

import { generateFontSize, getCompanyFont } from "react-lib/apps/HISV3/common/CompanyLogoForm";
import { HeaderLogoWithAddress } from "react-lib/apps/HISV3/common/HeaderPdfFormTemplate";

type TrainingResultType = {
  employee_code?: string;
  employee_name?: string;
  employee_position?: string;
  minimum_course_per_month?: string;
  minimum_hour_per_month?: string;
  minimum_score_per_month?: string;
  result_course_per_month: "FAIL" | "PASS";
  result_hour_per_month: "FAIL" | "PASS";
  result_score_per_month: "FAIL" | "PASS";
  score: string;
  summary_course_per_month?: string;
  summary_hour_per_month?: string;
};

export type SummaryKPIType = {
  account_for_course: number;
  account_for_hour: number;
  account_for_score: number;
  result: "FAIL" | "PASS";
  training_course: number;
  training_hour: number;
  training_score: number;
};

type FormTrainingKPIReportProps = {
  fields: TrainingResultType[];
  params: {
    date_range: string;
    division_name: string;
    printed_date: string;
  };
  summary: SummaryKPIType[];
};

const FormTrainingKPIReport = async (
  props: FormTrainingKPIReportProps
): Promise<TDocumentDefinitions> => {
  const font = getCompanyFont();
  const fontSizes = generateFontSize(font);

  const headerForm = await HeaderLogoWithAddress({
    font: getCompanyFont(),
    pageMargins: [0, 125, 0, 30],
    titleContent: [
      {
        alignment: "center",
        margin: [0, 5, 0, 0],
        style: { bold: true, fontSize: fontSizes[16] },
        text: `รายงานสรุป KPI เป้าหมายการอบรมของ แผนก ${props.params.division_name}`,
      },
      {
        alignment: "center",
        margin: [0, 0, 0, 10],
        style: { bold: true, fontSize: fontSizes[16] },
        text: `ประจำเดือน ${props.params.date_range}`,
      },
    ],
  });

  const { images, lineHeights, styles } = headerForm;

  const mainTableRows: TableCell[][] = [];

  for (const result of props.fields) {
    mainTableRows.push([
      { text: result.employee_code },
      { text: result.employee_name },
      { text: result.employee_position },
      { alignment: "center", text: result.minimum_course_per_month },
      { alignment: "center", text: result.summary_course_per_month },
      {
        alignment: "center",
        color: result.result_course_per_month === "PASS" ? "#22c55e" : "#ef4444",
        text: result.result_course_per_month,
      },
      { alignment: "center", text: result.minimum_hour_per_month },
      { alignment: "center", text: result.summary_hour_per_month },
      {
        alignment: "center",
        color: result.result_hour_per_month === "PASS" ? "#22c55e" : "#ef4444",
        text: result.result_hour_per_month,
      },
      { alignment: "center", text: result.minimum_score_per_month },
      { alignment: "center", text: result.score },
      {
        alignment: "center",
        color: result.result_score_per_month === "PASS" ? "#22c55e" : "#ef4444",
        text: result.result_score_per_month,
      },
    ]);
  }

  const summaryTableRows: TableCell[][] = [];

  for (const kpi of props.summary) {
    summaryTableRows.push([
      {
        alignment: "center",
        color: kpi.result === "PASS" ? "#22c55e" : "#ef4444",
        text: kpi.result,
      },
      { alignment: "center", text: String(kpi.training_course) },
      { alignment: "center", text: `${kpi.account_for_course}%` },
      { alignment: "center", text: String(kpi.training_hour) },
      { alignment: "center", text: `${kpi.account_for_hour}%` },
      { alignment: "center", text: String(kpi.training_score) },
      { alignment: "center", text: `${kpi.account_for_score}%` },
    ]);
  }

  return {
    defaultStyle: {
      font,
      fontSize: fontSizes[12],
      lineHeight: lineHeights[1],
    },
    pageOrientation: "landscape",
    pageSize: "A4",
    ...headerForm,
    images: { ...images },
    styles: {
      ...styles,
      footerData: {
        fontSize: fontSizes[10.5],
      },
      sectionHeader: {
        bold: true,
        margin: [0, 10, 0, 5],
      },
      tableHeader: {
        bold: true,
      },
    },
    content: [
      // Main training results table
      {
        margin: [15, 0, 15, 10],
        style: "tableStyle",
        table: {
          headerRows: 2,
          widths: ["8%", "14%", "10%", "8%", "8%", "6%", "8%", "8%", "6%", "8%", "8%", "8%"],
          body: [
            [
              {
                relativePosition: { y: 11 },
                rowSpan: 2,
                style: "tableHeader",
                text: "Code",
              },
              {
                relativePosition: { y: 11 },
                rowSpan: 2,
                style: "tableHeader",
                text: "Employee Name",
              },
              {
                relativePosition: { y: 11 },
                rowSpan: 2,
                style: "tableHeader",
                text: "Position",
              },
              {
                alignment: "center",
                colSpan: 3,
                style: "tableHeader",
                text: "Training Course/ month",
              },
              {},
              {},
              {
                alignment: "center",
                colSpan: 3,
                style: "tableHeader",
                text: "Training Hour/ month",
              },
              {},
              {},
              {
                alignment: "center",
                colSpan: 3,
                style: "tableHeader",
                text: "Training Score/ month",
              },
              {},
              {},
            ],
            [
              {},
              {},
              {},
              {
                alignment: "center",
                style: "tableHeader",
                text: "Required",
              },
              {
                alignment: "center",
                style: "tableHeader",
                text: "Participated",
              },
              {
                alignment: "center",
                style: "tableHeader",
                text: "Result",
              },
              {
                alignment: "center",
                style: "tableHeader",
                text: "Required",
              },
              {
                alignment: "center",
                style: "tableHeader",
                text: "Participated",
              },
              {
                alignment: "center",
                style: "tableHeader",
                text: "Result",
              },
              {
                alignment: "center",
                style: "tableHeader",
                text: "Required",
              },
              {
                alignment: "center",
                style: "tableHeader",
                text: "Achieved",
              },
              {
                alignment: "center",
                style: "tableHeader",
                text: "Result",
              },
            ],
            ...mainTableRows,
          ],
        },
        layout: {
          hLineColor: () => "#cccccc",
          hLineWidth: (index, node) => {
            if (index === 0 || index === 2 || index === node.table.body.length) {
              return 0.5;
            }

            return 0;
          },
          paddingBottom: (index) => (index === 0 ? 2 : 4),
          paddingTop: (index) => (index === 1 ? 2 : 4),
          vLineWidth: () => 0,
        },
      },

      // Summary KPI section
      {
        margin: [15, 20, 15, 5],
        style: "sectionHeader",
        text: "Summary KPI",
      },

      // Summary KPI table
      {
        alignment: "center",
        margin: [15, 0, 15, 10],
        style: "tableStyle",
        table: {
          headerRows: 1,
          widths: ["9.5%", "9.5%", "9.5%", "9.5%", "10%", "10%", "9.5%"],
          body: [
            [
              {
                alignment: "center",
                style: "tableHeader",
                text: "Result",
              },
              {
                alignment: "center",
                style: "tableHeader",
                text: "Training Course",
              },
              {
                alignment: "center",
                style: "tableHeader",
                text: "Account for (%)",
              },
              {
                alignment: "center",
                style: "tableHeader",
                text: "Training Hour",
              },
              {
                alignment: "center",
                style: "tableHeader",
                text: "Account for (%)",
              },
              {
                alignment: "center",
                style: "tableHeader",
                text: "Training Score",
              },
              {
                alignment: "center",
                style: "tableHeader",
                text: "Account for (%)",
              },
            ],
            ...summaryTableRows,
          ],
        },
        layout: {
          hLineColor: () => "#cccccc",
          hLineWidth: (index, node) => {
            if (index === 0 || index === 1 || index === node.table.body.length) {
              return 0.5;
            }

            return 0;
          },
          paddingBottom: () => 4,
          paddingTop: () => 4,
          vLineWidth: () => 0,
        },
      },
    ],
    footer(currentPage: number, pageCount: number) {
      return {
        columns: [
          {
            alignment: "left",
            margin: [0, 5, 0, 0],
            style: "footerData",
            text: `วันที่พิมพ์/Printed date: ${props.params.printed_date}`,
          },
          { text: "", width: "*" },
          {
            alignment: "right",
            margin: [0, 5, 0, 0],
            style: "footerData",
            text: `หน้า ${currentPage} จาก ${pageCount}`,
          },
        ],
        margin: [15, 0, 15, 10],
      };
    },
  };
};

export default FormTrainingKPIReport;
